import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import MethodologyAnchor from "../../components/services/MethodologyAnchor";
import Head from "next/head";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";

const ContactUsHome = dynamic(() => import("../../components/homepage/ContactUsHome"), { ssr: false });
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

Strategy.title = "Technology Strategy Consulting — Discovery Sprint & AI Roadmap | ITMTB";
Strategy.desc =
  "ITMTB runs fixed-price discovery sprints that produce a written roadmap, cost estimate, and risk register in 2 weeks. No open-ended retainer required to get started.";
Strategy.header_img = "https://www.itmtb.com/services/capability/strategy_1.webp";
Strategy.keywords =
  "technology strategy consulting India, paid discovery sprint India, AI strategy consulting India, digital transformation roadmap India, IT strategy India 2-week fixed price, technology roadmap enterprise India";

const faqItems = [
  {
    q: "What is a discovery sprint and what does it produce?",
    a: "A discovery sprint is a fixed-scope, fixed-price engagement that produces three deliverables in two weeks: a written technical roadmap (what to build, in what order, with what technology choices); a cost and timeline estimate for the recommended build; and a risk register that documents the technical, data, and organisational risks that could delay or derail the project, with mitigation recommendations. The discovery sprint exists because most technology engagements fail not in execution but in scoping — ambiguous requirements, underestimated complexity, and unidentified dependencies. A two-week sprint forces the clarity that prevents expensive mid-project course corrections. At the end of the sprint, you decide whether to proceed with the build — there is no obligation beyond the sprint fee. We have run discovery sprints for fintechs processing over 1 million transactions per 15 hours, global supply chain firms managing 100,000+ SKUs, and D2C brands rebuilding their digital operations.",
  },
  {
    q: "How do I know if my organisation is ready to deploy AI?",
    a: "The most reliable indicator of AI readiness is data accessibility — not data volume. Organisations that struggle with AI deployments almost always have the same root problem: the data the AI needs exists but can't be reliably retrieved. It's in a legacy system without an API, it's inconsistently formatted across sources, or it requires manual extraction. Before investing in model selection, prompt engineering, or AI platforms, the question to answer is: can we give an AI agent clean, structured, real-time access to the operational data it needs to act? If yes, you're ready to scope a use case. If no, data infrastructure comes first. We assess both dimensions during the discovery sprint: data readiness and use case fit. The output tells you specifically what's ready to automate now versus what requires a data infrastructure investment first.",
  },
  {
    q: "What does an AI strategy for an Indian enterprise actually involve?",
    a: "An AI strategy for an Indian enterprise has three components that generalist AI strategy frameworks often miss. First, regulatory mapping: DPDP Act 2023 affects any AI system that processes personal data of Indian citizens; RBI guidelines affect AI in banking and payment systems; SEBI guidelines affect AI in capital markets. Any AI strategy that doesn't account for the regulatory layer will hit compliance blockers mid-build. Second, data infrastructure gap analysis: Indian enterprise data tends to be distributed across legacy systems (Tally, older SAP versions, proprietary industry software) that weren't designed for API access. The strategy has to include a data readiness roadmap alongside the AI use case roadmap. Third, use case prioritisation for the Indian cost structure: the ROI threshold for AI automation in India is different from the US because labour costs are lower. Use cases that are unambiguously worth automating at $150/hour blended rates may not be at Indian rates — the strategy should be explicit about this arithmetic.",
  },
  {
    q: "What's the difference between a technology strategy and a digital transformation roadmap?",
    a: "A technology strategy is a set of decisions about what technology to use, how to organise technology investment, and what to build versus buy versus partner. It answers: what stack, what architecture, what capabilities in-house versus outsourced, what the 12–18 month build priority is. A digital transformation roadmap is broader: it includes the technology decisions but also the organisational changes, process redesigns, and change management required to make technology adoption stick. The roadmap typically covers 2–3 years and includes dependency sequencing — what has to happen before what else can happen. We produce technology strategy as the output of a discovery sprint. A digital transformation roadmap is produced for organisations with a more complex stakeholder environment or multi-business-unit scope — it takes longer and involves more workshops with different parts of the organisation.",
  },
  {
    q: "How do you approach build vs. buy vs. partner decisions for enterprise software?",
    a: "We use a decision framework with three criteria. First, strategic differentiation: if the capability is a direct competitive differentiator (a proprietary risk model, a custom logistics platform, an AI feature that no competitor has), build — because owning the IP matters. If it's commodity infrastructure, buy or use a managed service. Second, total cost of ownership over 3 years, not just licence vs. development cost: custom-built solutions have ongoing maintenance costs; SaaS has escalating subscription costs and vendor lock-in risk. We build the 3-year TCO model for the material options. Third, data control: some AI use cases require keeping sensitive data in your own environment, which eliminates certain SaaS options regardless of cost. The discovery sprint produces a concrete build/buy/partner recommendation with the reasoning documented — not a generic 2×2 matrix.",
  },
  {
    q: "How does ITMTB price an engagement?",
    a: "Pricing follows the engagement model. Discovery sprints are fixed-price for a fixed timebox. Build engagements are scoped after discovery — fixed-scope, time-and-materials, or outcome-linked. Managed services are billed monthly with defined SLAs. Total cost depends on scope, integration complexity, QA depth, and post-launch support — we publish a transparent breakdown for buyers evaluating their options.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
  {
    q: "What does an engagement with ITMTB look like?",
    a: "Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically 2 weeks. You leave with a written roadmap, risk register, cost estimate, and timeline before committing to a build. We then offer four engagement models: fixed-scope builds, time-and-materials, outcome-linked retainers, and managed services post-launch. If discovery doesn't justify proceeding, you can stop there — no obligation.",
    link: "/itmtb-how-we-work",
    linkLabel: "See how we work",
  },
];
Strategy.faq = faqItems;

export default function Strategy() {
  let insights = blogData.filter(item => !["case-study", "tech"].includes(item?.meta?.category));
  let caseStudies = blogData.filter(item => item?.meta?.category === "case-study");

  let filteredInsights = insights.filter(item => item?.meta?.refer?.includes("Strategy"));
  let filteredCaseStudies = caseStudies.filter(item => item?.meta?.refer?.includes("Strategy"));

  if (filteredInsights.length === 1) {
    insights = [...filteredInsights, ...getRandom(insights.filter(item => !filteredInsights.includes(item)), 1)];
  } else if (filteredInsights.length >= 2) {
    insights = getRandom(filteredInsights, 2);
  } else {
    insights = getRandom(insights, 2);
  }

  if (filteredCaseStudies.length === 1) {
    caseStudies = [...filteredCaseStudies, ...getRandom(caseStudies.filter(item => !filteredCaseStudies.includes(item)), 1)];
  } else if (filteredCaseStudies.length >= 2) {
    caseStudies = getRandom(filteredCaseStudies, 2);
  } else {
    caseStudies = getRandom(caseStudies, 2);
  }

  const subServices = [
    {
      img: null,
      key: "Discovery Sprint — Two Weeks, Fixed Price",
      title: "Roadmap, cost estimate, and risk register. You decide whether to build. No obligation beyond the sprint.",
      text: "Before committing to a build, you get clarity on exactly what to build, how long it will take, what it will cost, and what could go wrong. The discovery sprint forces that clarity into writing in two weeks.\n\nWe have run discovery sprints for fintechs processing over 1 million transactions per 15 hours, global supply chain firms, and D2C brands. The output is a document you can act on immediately — or use to evaluate other vendors.",
    },
    {
      img: null,
      key: "AI Strategy and Use Case Roadmap",
      title: "Which workflows are worth automating, which aren't, and what you need in place before the build starts.",
      text: "An AI strategy grounded in your actual operations, not vendor pitch decks. We assess use case fit (is this process structured enough, high-volume enough, and data-accessible enough to be worth automating?), data readiness (can an AI agent reach the data it needs?), and regulatory implications (DPDP, RBI, SEBI where applicable).\n\nOutput: a prioritised use case list with ROI estimates, data readiness gaps, and a sequenced build roadmap.",
    },
    {
      img: null,
      key: "Technology Stack Audit",
      title: "An independent assessment of what you have, what it can do, and what you should change.",
      text: "We audit existing technology stacks for enterprises considering a major investment: pre-AI deployment (is the current stack ready?), pre-acquisition (what is the technical liability?), or pre-rebuild (is a rebuild actually warranted?).\n\nOutput: a written assessment covering architecture, security posture, scalability limits, technical debt inventory, and a prioritised list of changes with estimated effort — the same format as the discovery sprint deliverable.",
    },
    {
      img: null,
      key: "Digital Transformation Roadmap",
      title: "Technology decisions plus the organisational sequencing required to make adoption stick.",
      text: "For organisations with multi-business-unit scope or complex stakeholder environments, a technology strategy alone isn't sufficient — the roadmap needs to include process redesign, change management dependencies, and a realistic sequencing of what happens before what.\n\nWe produce transformation roadmaps for 12–24 month horizons, with quarterly milestones and explicit dependency mapping — so leadership can track progress and adjust investment based on results.",
    },
  ];

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              name: "Technology Strategy Consulting — Discovery Sprint & AI Roadmap India",
              url: "https://www.itmtb.com/services/strategy",
              image: "https://www.itmtb.com/services/capability/strategy_1.webp",
              description:
                "ITMTB runs fixed-price discovery sprints that produce a written roadmap, cost estimate, and risk register in 2 weeks. No open-ended retainer required to get started.",
              provider: {
                "@type": "Organization",
                name: "ITMTB Technologies",
                url: "https://www.itmtb.com",
                logo: "https://www.itmtb.com/logo.webp",
              },
              areaServed: [{ "@type": "Place", name: "India" }],
              serviceType: [
                "Technology Strategy Consulting India",
                "Paid Discovery Sprint",
                "AI Strategy Consulting India",
                "Digital Transformation Roadmap",
                "IT Strategy Consulting India",
                "Technology Roadmap Enterprise India",
                "AI Readiness Assessment",
                "Risk Register and Cost Estimation",
              ],
              sameAs: [
                "https://www.linkedin.com/company/itmtb-technologies/",
                "https://x.com/ITMTBTech",
                "https://www.instagram.com/itmtbtech/",
              ],
            }),
          }}
        />
      </Head>

      <Service2
        heading="Technology Strategy and Discovery"
        paragraph={`Two weeks. Fixed price. Roadmap, cost estimate, and risk register — or you walk.

Most technology projects fail in scoping, not execution: ambiguous requirements, underestimated complexity, and risks nobody documented until they became blockers. A discovery sprint forces that clarity into writing before you commit to a build.

We audited the complete tech stack of a fintech processing over 1 million transactions every 15 hours — and delivered actionable findings within a 2-week fixed-scope engagement. The same sprint format works for AI use case scoping, pre-build technology audits, and digital transformation roadmaps.`}
        bgPath="/services/capability/strategy_1.webp"
        primaryCta={{ label: "Talk to us →", href: "/contact-us" }}
      />

      <SuccessStoryEmbed
        referTag="Strategy"
        maxItems={3}
        heading="Selected work in strategy and advisory"
      />

      <CustomerSection title="Trusted by" />

      <MethodologyAnchor />

      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      <ServiceFAQ items={faqItems} />

      <ServiceEngagementLinks />

      <ContactUsHome />

      <Footer />
    </div>
  );
}
